import { flexRender, type Table } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "~/shared/lib/utils";
import { SECTION_V5 } from "./design-tokens";

export interface SectionDataTableProps<TRow extends { id: string }> {
    table: Table<TRow>;
    onRowClick?: (row: TRow) => void;
    getRowClassName?: (row: TRow) => string | undefined;
}

/** Desktop table render for `SectionDataView` (SECTION_V5 tokens, server-side sort). */
export function SectionDataTable<TRow extends { id: string }>({
    table,
    onRowClick,
    getRowClassName,
}: SectionDataTableProps<TRow>) {
    return (
        <div className="w-full overflow-x-auto px-0 py-2">
            <table className={SECTION_V5.tableRoot}>
                <thead className={SECTION_V5.tableHead}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                const section = header.column.columnDef.meta?.section;
                                const canSort = header.column.getCanSort();
                                const sorted = header.column.getIsSorted();
                                return (
                                    <th
                                        key={header.id}
                                        className={cn(
                                            SECTION_V5.tableHeadCell,
                                            section?.hideAt && SECTION_V5.tableCellHidden[section.hideAt],
                                            canSort && "cursor-pointer select-none hover:text-foreground",
                                            section?.headClassName,
                                        )}
                                        onClick={
                                            canSort ? header.column.getToggleSortingHandler() : undefined
                                        }
                                    >
                                        <div className="flex items-center gap-1">
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef.header,
                                                      header.getContext(),
                                                  )}
                                            {canSort &&
                                                (sorted === "asc" ? (
                                                    <ArrowUp className="size-3.5" aria-hidden />
                                                ) : sorted === "desc" ? (
                                                    <ArrowDown className="size-3.5" aria-hidden />
                                                ) : (
                                                    <ArrowUpDown
                                                        className="size-3.5 opacity-40"
                                                        aria-hidden
                                                    />
                                                ))}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr
                            key={row.id}
                            className={cn(
                                SECTION_V5.tableRow,
                                onRowClick && "cursor-pointer",
                                getRowClassName?.(row.original),
                            )}
                            onClick={() => onRowClick?.(row.original)}
                        >
                            {row.getVisibleCells().map((cell) => {
                                const section = cell.column.columnDef.meta?.section;
                                return (
                                    <td
                                        key={cell.id}
                                        className={cn(
                                            SECTION_V5.tableCell,
                                            section?.hideAt && SECTION_V5.tableCellHidden[section.hideAt],
                                            section?.cellClassName,
                                        )}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
